import type { ServiceLineOption } from "../../type";
import type { ServiceLineAllNode, ServiceLineTreeNode } from "./serviceLineTreeTypes";

export function isCanonicalServiceLineAllNode(node: ServiceLineTreeNode): boolean {
  return (
    node.space_organization_unit_id === null &&
    node.name.trim().toLowerCase() === "all"
  );
}

export function normalizeServiceLineTreeNode(raw: ServiceLineTreeNode): ServiceLineTreeNode {
  const rawChildren = raw.children;
  const children = Array.isArray(rawChildren)
    ? rawChildren.map((c) => normalizeServiceLineTreeNode(c))
    : [];
  const id = raw.space_organization_unit_id;
  return {
    name: typeof raw.name === "string" ? raw.name : "",
    space_organization_unit_id:
      id === null || id === undefined ? null : Number(id),
    children,
  };
}

export function normalizeServiceLineRoots(roots: ServiceLineTreeNode[]): ServiceLineTreeNode[] {
  return roots.map((r) => normalizeServiceLineTreeNode(r));
}

function flattenSelectableOptions(nodes: ServiceLineTreeNode[]): ServiceLineOption[] {
  return nodes.flatMap((n) => {
    const children = n.children ?? [];
    if (isCanonicalServiceLineAllNode(n) && children.length === 0) {
      return [{ label: n.name, value: "" }];
    }
    if (isCanonicalServiceLineAllNode(n)) {
      return [{ label: n.name, value: "" }, ...flattenSelectableOptions(children)];
    }
    if (n.space_organization_unit_id === null) {
      return flattenSelectableOptions(children);
    }
    const self: ServiceLineOption = {
      label: n.name,
      value: String(n.space_organization_unit_id),
    };
    return [self, ...flattenSelectableOptions(children)];
  });
}

export function buildServiceLineOptionsFromPayload(
  roots: ServiceLineTreeNode[],
): ServiceLineOption[] {
  return flattenSelectableOptions(roots);
}

export function buildServiceLinePrefillOption(
  prefill_data: ServiceLineAllNode,
): ServiceLineOption {
  return {
    label: prefill_data.name,
    value:
      prefill_data.space_organization_unit_id === null 
      || prefill_data.space_organization_unit_id === undefined
        ? ""
        : String(prefill_data.space_organization_unit_id),
  };
}

function findCanonicalAllName(roots: ServiceLineTreeNode[]): string | undefined {
  for (const r of roots) {
    if (isCanonicalServiceLineAllNode(r) && (r.children?.length ?? 0) === 0) {
      return r.name;
    }
  }
  return undefined;
}

function findNodeName(roots: ServiceLineTreeNode[], id: number): string | undefined {
  for (const r of roots) {
    if (
      r.space_organization_unit_id !== null &&
      r.space_organization_unit_id === id
    ) {
      return r.name;
    }
    const nested = findNodeName(r.children ?? [], id);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

export function getDisplayLabelForSelectedUnit(
  roots: ServiceLineTreeNode[] | null,
  serviceLineId: string,
): string {
  if (!roots?.length) {
    return "";
  }
  if (!serviceLineId) {
    return findCanonicalAllName(roots) ?? "";
  }
  const id = Number(serviceLineId);
  if (Number.isNaN(id)) {
    return "";
  }
  return findNodeName(roots, id) ?? "";
}
